import { ReactNode, useId } from 'react'
import { Button, PageControlBarModule, SearchableSelect, EmissionsSummaryPanelModule } from '../../components/BaseComponents'
import { CarbonAccountSummary } from '../../types/CarbonAccountSummary'
import { getCurrencySymbol } from '../../lib/utils'
import useCurrency from '../../hooks/useCurrency'
import JurisdictionSelect from '../../components/BaseComponents/JurisdictionSelect'


export type PortfolioDetailZones = {
	primarySelector: { label: string; controlId: string; children: ReactNode }
	summary: ReactNode
	filtersSimple: ReactNode
	actions: ReactNode
	table: ReactNode
	supplementary: ReactNode
}

type UsePortfolioDetailZonesArgs = {
	portfolioSelector: ReactNode
	portfolioName: string
	summary: any
	isLoadingSummary: boolean
	summaryError?: any
	onEditClick: () => void
	organisationNameValue: string
	organisationNamePlaceholder: string
	onOrganisationNameChange: (value: string) => void
	onOrganisationNameKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void
	organisationNumberValue: string
	organisationNumberPlaceholder: string
	onOrganisationNumberChange: (value: string) => void
	onOrganisationNumberKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void
	jurisdictionValue: string
	onJurisdictionChange: (value: string) => void
	publicBodyOptions: Array<{ value: string; label: string }>
	publicBodyValue: string
	onPublicBodyChange: (value: string) => void
	yearOptions: Array<{ value: string; label: string }>
	yearValue: string
	onYearChange: (value: string) => void
	onClearFilters: () => void
	tableComponent: ReactNode
	supplementaryComponent: ReactNode
}

export function usePortfolioDetailZones({
	portfolioSelector,
	portfolioName,
	summary,
	isLoadingSummary,
	summaryError,
	onEditClick,
	organisationNameValue,
	organisationNamePlaceholder,
	onOrganisationNameChange,
	onOrganisationNameKeyDown,
	organisationNumberValue,
	organisationNumberPlaceholder,
	onOrganisationNumberChange,
	onOrganisationNumberKeyDown,
	jurisdictionValue,
	onJurisdictionChange,
	publicBodyOptions,
	publicBodyValue,
	onPublicBodyChange,
	yearOptions,
	yearValue,
	onYearChange,
	onClearFilters,
	tableComponent,
	supplementaryComponent,
}: UsePortfolioDetailZonesArgs): PortfolioDetailZones {
	const currency = useCurrency()
	const portfolioSelectorId = useId()

	const errorText = (error: any): string | undefined => {
		if (error === undefined || error === null) {
			return undefined
		}
		if (typeof error === 'string') {
			return error
		}
		return error.message ?? String(error)
	}

	return {
		primarySelector: {
			label: 'Select Portfolio',
			controlId: portfolioSelectorId,
			children: portfolioSelector,
		},
		summary: (
			<EmissionsSummaryPanelModule
				carbonSummary={{
					intensityRatio: String(summary?.intensity ?? 0),
					localIntensityRatio: String(summary?.intensity ?? 0),
					scope1Total: String(summary?.scope1Total ?? 0),
					scope2Total: String(summary?.scope2Total ?? 0),
					scope3Total: String(summary?.scope3Total ?? 0),
					scope1Completion: String(summary?.scope1Completion ?? 0),
					scope2Completion: String(summary?.scope2Completion ?? 0),
					scope3Completion: String(summary?.scope3Completion ?? 0),
					totalCompletion: String(summary?.totalCompletion ?? 0),
				} as CarbonAccountSummary}
				totalEmissions={summary?.totalEmissions}
				intensityRatioUnit={`${getCurrencySymbol(currency)} of portfolio value`}
				loading={isLoadingSummary && !summary}
				error={errorText(summaryError)}
			/>
		),
		filtersSimple: (
			<>
				{/* Group 1: Search fields (left) + Edit portfolio button (right) */}
				<div className='flex flex-wrap gap-x-5 gap-y-4 items-end w-full'>
					<PageControlBarModule.Search
						name='organisationName'
						label='Search by organisation name'
						placeholder={organisationNamePlaceholder}
						value={organisationNameValue}
						onChange={(e) => onOrganisationNameChange(e.target.value)}
						onKeyDown={onOrganisationNameKeyDown}
						className='w-[300px]'
					/>
					<PageControlBarModule.Search
						name='organisationNumber'
						label='Search by organisation number'
						placeholder={organisationNumberPlaceholder}
						value={organisationNumberValue}
						onChange={(e) => onOrganisationNumberChange(e.target.value)}
						onKeyDown={onOrganisationNumberKeyDown}
						className='w-[300px]'
					/>
					<div className='grow' />
					<PageControlBarModule.Button
						requiresWrite
						type='Primary'
						text='Edit portfolio'
						icon='fa-regular fa-pen-to-square'
						hasLeadingIcon
						onClick={onEditClick}
					/>
				</div>

				{/* Group 2: Filter dropdowns (left) + action buttons (right) — collapsible in future */}
				<div className='flex flex-wrap gap-x-5 gap-y-4 items-end w-full'>
					<div className='flex flex-col justify-end'>
						<JurisdictionSelect
							value={jurisdictionValue}
							onChange={onJurisdictionChange}
						/>
					</div>
					<div className='flex flex-col justify-end'>
						<SearchableSelect
							isSearchable={false}
							options={publicBodyOptions}
							value={publicBodyOptions.find((option) => option.value === publicBodyValue)}
							onChange={(option) => onPublicBodyChange(option?.value ?? '')}
							classNamePrefix='react-select'
						/>
					</div>
					<div className='flex flex-col justify-end'>
						<SearchableSelect
							options={yearOptions}
							value={yearOptions.find((option) => option.value === yearValue)}
							onChange={(option) => onYearChange(option?.value ?? '')}
							classNamePrefix='react-select'
						/>
					</div>
					<div className='grow' />
					<PageControlBarModule.Button
						type='Primary'
						text='Chart views'
						icon='fa-solid fa-chart-simple'
						hasLeadingIcon
						disabled
					/>
					<PageControlBarModule.Button
						type='Primary'
						text='Clear filters'
						icon='fa-solid fa-filter-circle-xmark'
						hasLeadingIcon
						onClick={onClearFilters}
					/>
				</div>
			</>
		),
		actions: undefined,
		table: tableComponent,
		supplementary: supplementaryComponent,
	}
}
